import type { SerializedStyles } from "@emotion/react";
import { useState } from "react";
import type { FC, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import { Button, DropdownButton, Tooltip } from "@aviary";
import { isEmailValid } from "@shared/components/EmailInput";
import { useBreakpoints } from "@shared/hooks";
import { gRecaptchaExecute } from "@shared/utils";
import { useGlobalConfig } from "@unauthenticated/shared/context";
import type { PractitionerType } from "@unauthenticated/shared/data/types";
import { useAccountExistsCheck } from "@unauthenticated/shared/hooks";
import { l } from "@unauthenticated/shared/locales/i18n";

import { DesignationDropdown } from "../DesignationDropdown";
import { DesignationTooltip } from "../DesignationTooltip";
import { FirstNameInput } from "../FirstNameInput";
import { LastNameInput } from "../LastNameInput";
import { IsNameValid } from "../NameValidation";
import { PasswordInput, isPasswordValid } from "../PasswordInput";
import { PractitionerSignUpEmailInput } from "../PractitionerSignUpEmailInput";
import { TermsOfService } from "../TermsOfService";

import * as styles from "./PractitionerSignUpForm.styles";

type SubmitValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  practitionerTypeId: string;
  gToken: string;
  pageVersion: string;
  signUpPage: string;
};

type Errors = {
  email: string[];
  firstName: string[];
  lastName: string[];
  password: string[];
  practitionerTypeId: string[];
};

interface Props {
  isPeerReferral?: boolean;
  initialFirstName?: string;
  initialLastName?: string;
  initialEmail?: string;
  onSubmit: (values: SubmitValues) => void;
  displayDesignation?: boolean;
  requireDesignation?: boolean;
  practitionerTypes?: PractitionerType[];
  designationId: string;
  setDesignationId: (string) => void;
  errors?: Errors;
  isLoading: boolean;
  pageVersion: string;
  signUpPage: string;
  logField: (field: string) => void;
  overrideLinkStyles?: SerializedStyles;
  onSignInRedirect?: () => void;
}

const PractitionerSignUpForm: FC<Props> = ({
  isPeerReferral,
  initialFirstName,
  initialLastName,
  initialEmail,
  displayDesignation,
  requireDesignation,
  practitionerTypes,
  designationId,
  setDesignationId,
  onSubmit,
  isLoading,
  errors,
  pageVersion,
  signUpPage,
  logField,
  overrideLinkStyles,
  onSignInRedirect,
}) => {
  const { emailRegex } = useGlobalConfig();

  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [firstName, setFirstName] = useState(initialFirstName);
  const [firstNameDirty, setFirstNameDirty] = useState(false);
  const [lastName, setLastName] = useState(initialLastName);
  const [lastNameDirty, setLastNameDirty] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [emailDirty, setEmailDirty] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);

  const {
    isAccountExists,
    setIsAccountExists,
    handleCheckIfAccountExists,
    checkIfAccountExistsWithRecaptcha,
  } = useAccountExistsCheck();

  const handleEmailInputBlur = e => {
    if (isEmailValid(email, emailRegex)) {
      logField("email");
    }
    setEmailDirty(true);
    if (e?.target.value) {
      setEmail(e.target.value);
      handleCheckIfAccountExists(e.target.value);
    }
  };

  const areFieldsValid = () => {
    const passwordValid = isPasswordValid(password);
    const firstNameValid = IsNameValid(firstName);
    const lastNameValid = IsNameValid(lastName);
    const emailValid = isEmailValid(email, emailRegex) && !isAccountExists;

    return passwordValid && firstNameValid && lastNameValid && emailValid;
  };

  const isDesignationValid = () => {
    const needsValidDesignation = displayDesignation || requireDesignation;
    const designationValid = needsValidDesignation ? !!designationId : true;

    return designationValid;
  };

  const isFormValid = () => areFieldsValid() && isDesignationValid();

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    if (!isFormValid()) {
      setFirstNameDirty(true);
      setLastNameDirty(true);
      setEmailDirty(true);
      setPasswordDirty(true);

      return;
    }

    gRecaptchaExecute(gToken => {
      onSubmit({
        firstName,
        lastName,
        email,
        password,
        practitionerTypeId: designationId,
        gToken,
        pageVersion,
        signUpPage,
      });
    });
  };

  const renderDesignationDropdown = () =>
    displayDesignation && (
      <div css={styles.designationRow}>
        <DesignationDropdown
          practitionerTypes={practitionerTypes}
          setValue={(id: string) => {
            if (id.length > 0) {
              logField("practitioner_type");
            }
            setDesignationId(id);
          }}
          css={styles.marginBottom}
        >
          <DropdownButton
            isLoading={isLoading}
            css={styles.textAlign}
            data-e2e="practitioner-type-trigger"
          >
            {getTriggerText()}
          </DropdownButton>
        </DesignationDropdown>
        {tablet.greaterThan && <DesignationTooltip />}
      </div>
    );

  const getTriggerText = () => {
    const designation = practitionerTypes.find(type => type.id === designationId);

    return designation?.description || t(l.common.IAmA);
  };

  const showTooltip = () => requireDesignation && !isDesignationValid() && areFieldsValid();

  return (
    <form
      onSubmit={handleSubmit}
      css={styles.form}
      noValidate
      data-e2e="practitioner-sign-up-form"
      data-gtm-id="prac-sign-up-form"
    >
      <div css={styles.Row}>
        <FirstNameInput
          disabled={!!initialFirstName && !isPeerReferral}
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          wrapperStyles={styles.margin}
          required
          isDirty={firstNameDirty}
          onBlur={() => {
            if (firstName.length > 0) {
              logField("first_name");
            }
            setFirstNameDirty(true);
          }}
          errors={errors?.firstName}
          isLoading={isLoading}
        />
        <LastNameInput
          disabled={!!initialLastName && !isPeerReferral}
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          required
          isDirty={lastNameDirty}
          onBlur={() => {
            if (lastName.length > 0) {
              logField("last_name");
            }
            setLastNameDirty(true);
          }}
          errors={errors?.lastName}
          isLoading={isLoading}
        />
      </div>
      <PractitionerSignUpEmailInput
        checkIfAccountExistsWithRecaptcha={checkIfAccountExistsWithRecaptcha}
        isAccountExists={isAccountExists}
        setIsAccountExists={setIsAccountExists}
        handleCheckIfAccountExists={handleCheckIfAccountExists}
        setEmail={setEmail}
        disabled={!!initialEmail && !isPeerReferral}
        required
        value={email}
        onChange={e => setEmail(e.target.value)}
        wrapperStyles={styles.marginBottom}
        onBlur={handleEmailInputBlur}
        isDirty={emailDirty}
        isLoading={isLoading}
        onSignInRedirect={onSignInRedirect}
      />
      <PasswordInput
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
        isDirty={passwordDirty}
        onFocus={() => setPasswordDirty(true)}
        onBlur={() => {
          if (isPasswordValid(password)) {
            logField("password");
          }
        }}
        errors={errors?.password}
        isLoading={isLoading}
      />
      {renderDesignationDropdown()}
      <TermsOfService overrideLinkStyles={overrideLinkStyles} css={styles.serviceTerms} />
      <Tooltip
        isTooltipHidden={!showTooltip()}
        placement="bottom"
        tooltipContent={t(l.practitionerSignUp.SelectPractitioner)}
        data-testid="disabled-submit-tooltip"
      >
        <Button
          type="submit"
          disabled={!isFormValid()}
          isLoading={isLoading}
          data-e2e="create-practitioner-button"
        >
          {t(l.common.CreateMyAccount)}
        </Button>
      </Tooltip>
    </form>
  );
};

PractitionerSignUpForm.defaultProps = {
  initialFirstName: "",
  initialLastName: "",
  initialEmail: "",
};

export { PractitionerSignUpForm };
export type { SubmitValues };
